/**
 * Support API — real backend (tenant server, tenant-isolated).
 *
 * Every call goes through `frappeMethod` → the JWT-authed tenant-server client,
 * which unwraps Frappe's `{ message }` envelope. The backend
 * (`zaviago_backend.api.support`) already returns camelCase shapes that match the
 * types in `../types`, so no key remapping is needed here.
 *
 * Inline <img>/<video> that the composer inserted as `blob:` object URLs are
 * uploaded via `upload_ticket_media` (private files, attached to the ticket so
 * their access follows the ticket's tenant permission) and the HTML is rewritten
 * to the persistent `/files/` URLs *before* the ticket/reply is sent — so the
 * whole media flow stays inside this file and no component changes.
 *
 *   listMyTickets   → zaviago_backend.api.support.list_my_tickets
 *   getTicket       → zaviago_backend.api.support.get_ticket
 *   createTicket    → zaviago_backend.api.support.create_ticket
 *   addReply        → zaviago_backend.api.support.add_reply
 *   setStatus       → zaviago_backend.api.support.update_ticket
 *   setSatisfaction → zaviago_backend.api.support.set_satisfaction
 */

import { frappeMethod } from "@/lib/erp/utils";
import { getERPClient } from "@/lib/erp/client";
import type {
  CreateTicketInput,
  TenantGroup,
  Ticket,
  TicketCategory,
  TicketMessage,
  TicketPriority,
  TicketStatus,
  TicketSummary,
} from "../types";

const API = "zaviago_backend.api.support";

// ── inline media ────────────────────────────────────────────────────────────
async function uploadTicketMedia(
  file: File,
): Promise<{ file_url: string; file_name: string }> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return getERPClient().callWithFormData<{ file_url: string; file_name: string }>(
    `${API}.upload_ticket_media`,
    fd,
  );
}

/**
 * Replace the composer's `blob:` object URLs with uploaded, tenant-scoped
 * `/files/` URLs. Object URLs created by the composer are still resolvable via
 * fetch at submit time (they're never revoked), so we can read the bytes back.
 * If an upload fails we leave the blob URL in place — the message still sends
 * with its text intact rather than blocking on a bad image.
 */
async function persistInlineMedia(html?: string): Promise<string | undefined> {
  if (!html || !html.includes("blob:")) return html;
  const blobUrls = [
    ...new Set(
      [...html.matchAll(/(?:src|href)="(blob:[^"]+)"/g)].map((m) => m[1]),
    ),
  ];
  let out = html;
  for (const blobUrl of blobUrls) {
    try {
      const blob = await (await fetch(blobUrl)).blob();
      const ext = (blob.type.split("/")[1] || "bin").split(";")[0];
      const file = new File([blob], `upload-${Date.now()}.${ext}`, {
        type: blob.type,
      });
      const { file_url } = await uploadTicketMedia(file);
      out = out.split(blobUrl).join(file_url);
    } catch {
      // leave the blob URL; text still sends
    }
  }
  return out;
}

// ── endpoints ─────────────────────────────────────────────────────────────
export async function listMyTickets(): Promise<TicketSummary[]> {
  return frappeMethod<TicketSummary[]>(`${API}.list_my_tickets`);
}

export async function getTicket(name: string): Promise<Ticket | null> {
  return frappeMethod<Ticket>(`${API}.get_ticket`, { name });
}

export async function createTicket(input: CreateTicketInput): Promise<Ticket> {
  const bodyHtml = await persistInlineMedia(input.bodyHtml);
  return frappeMethod<Ticket>(`${API}.create_ticket`, {
    subject: input.subject,
    body: input.body,
    bodyHtml,
    priority: input.priority,
    category: input.category,
    context: input.context ? JSON.stringify(input.context) : undefined,
  });
}

export async function addReply(
  name: string,
  body: string,
  bodyHtml?: string,
  replyTo?: TicketMessage["replyTo"],
): Promise<TicketMessage> {
  const html = await persistInlineMedia(bodyHtml);
  const ticket = await frappeMethod<Ticket>(`${API}.add_reply`, {
    name,
    body,
    bodyHtml: html,
    replyTo: replyTo ? JSON.stringify(replyTo) : undefined,
  });
  // Backend returns the full ticket; the contract wants the new (last) message.
  return ticket.messages[ticket.messages.length - 1];
}

export async function setStatus(name: string, status: TicketStatus): Promise<Ticket> {
  return frappeMethod<Ticket>(`${API}.update_ticket`, { name, status });
}

/** Set priority. `asAgent` routes to the john-only cross-tenant endpoint;
 *  otherwise the tenant-scoped update_ticket (customer editing own ticket). */
export async function setPriority(
  name: string,
  priority: TicketPriority,
  asAgent = false,
): Promise<Ticket> {
  return asAgent
    ? frappeMethod<Ticket>(`${AGENT_API}.set_priority`, { name, priority })
    : frappeMethod<Ticket>(`${API}.update_ticket`, { name, priority });
}

/** Set category/type. See setPriority for the asAgent routing. */
export async function setCategory(
  name: string,
  category: TicketCategory,
  asAgent = false,
): Promise<Ticket> {
  return asAgent
    ? frappeMethod<Ticket>(`${AGENT_API}.set_category`, { name, category })
    : frappeMethod<Ticket>(`${API}.update_ticket`, { name, category });
}

export async function setSatisfaction(
  name: string,
  rating: "up" | "down",
): Promise<Ticket> {
  return frappeMethod<Ticket>(`${API}.set_satisfaction`, { name, rating });
}

// ── agent receiving inbox (john-only, cross-tenant) ──────────────────────────
// Counterpart to the tenant-isolated endpoints above: ONE owner account
// (see SUPPORT_AGENT_USER) sees every store's incoming tickets in one inbox.
// The backend (zaviago_backend.api.support_agent) hard-gates on session.user,
// so every other user only ever hits the tenant-scoped calls above.
const AGENT_API = "zaviago_backend.api.support_agent";

/** The single account that gets the cross-store support receiving inbox. */
export const SUPPORT_AGENT_USER = "john@zaviago.com";

/** True when this user is the support owner (sees ALL stores' tickets). */
export function isSupportReceiver(username?: string | null): boolean {
  return (username ?? "").trim().toLowerCase() === SUPPORT_AGENT_USER;
}

/**
 * All incoming tickets across every tenant, newest activity first (john-only).
 * Optional SERVER-SIDE filters (so the inbox scales to many customers):
 *   - `tenants`: stall ids to INCLUDE (a business selection is expanded to its
 *     stall ids by the caller). Empty/undefined = every tenant.
 *   - `statuses`: customer-facing statuses to KEEP. Empty/undefined = all statuses.
 */
export async function listIncoming(opts?: {
  tenants?: string[];
  statuses?: TicketStatus[];
}): Promise<TicketSummary[]> {
  const params: Record<string, string> = {};
  if (opts?.tenants?.length) params.tenants = JSON.stringify(opts.tenants);
  if (opts?.statuses?.length) params.statuses = JSON.stringify(opts.statuses);
  return frappeMethod<TicketSummary[]>(`${AGENT_API}.list_incoming`, params);
}

/** Grouped tenant/stall structure (Company ▸ Stalls) for the inbox filter. john-only. */
export async function listIncomingTenants(): Promise<TenantGroup[]> {
  return frappeMethod<TenantGroup[]>(`${AGENT_API}.list_incoming_tenants`);
}

/** Full thread for ANY tenant's ticket (john-only). */
export async function getIncomingTicket(name: string): Promise<Ticket | null> {
  return frappeMethod<Ticket>(`${AGENT_API}.get_incoming_ticket`, { name });
}

/** Set the PRIVATE agent-only internal status (customer never sees it). john-only. */
export async function setInternalStatus(
  name: string,
  status: string,
): Promise<{ name: string; internalStatus: string }> {
  return frappeMethod(`${AGENT_API}.set_internal_status`, { name, status });
}

/** Set the customer-facing status across any tenant. john-only. */
export async function setExternalStatus(name: string, status: string): Promise<Ticket> {
  return frappeMethod<Ticket>(`${AGENT_API}.set_external_status`, { name, status });
}

/**
 * Reply to ANY tenant's ticket as the Zaviago Support Team (agent voice, never
 * John's personal account). john-only. Inline images are uploaded the same way
 * customer replies are (public /files/ URLs) before send.
 */
export async function addAgentReply(
  name: string,
  body: string,
  bodyHtml?: string,
): Promise<Ticket> {
  const html = await persistInlineMedia(bodyHtml);
  return frappeMethod<Ticket>(`${AGENT_API}.add_agent_reply`, { name, body, bodyHtml: html });
}

/**
 * Edit the text of an existing AGENT message on ANY tenant's ticket. john-only.
 * The backend refuses anything that isn't sender_type='agent' (editing a
 * customer's own words is a footgun). Inline images are persisted like a reply.
 */
export async function editAgentMessage(
  name: string,
  messageId: string,
  body: string,
  bodyHtml?: string,
): Promise<Ticket> {
  const html = await persistInlineMedia(bodyHtml);
  return frappeMethod<Ticket>(`${AGENT_API}.edit_message`, {
    name,
    message_id: messageId,
    body,
    bodyHtml: html,
  });
}

/** Delete ANY message from ANY tenant's ticket (John's moderation power). john-only. */
export async function deleteMessage(name: string, messageId: string): Promise<Ticket> {
  return frappeMethod<Ticket>(`${AGENT_API}.delete_message`, { name, message_id: messageId });
}

/**
 * Save (overwrite) the agent-only UNSENT draft on a ticket. john-only. Writes only
 * the draft fields — never appends a message or touches the customer-facing status —
 * so the customer sees nothing. Inline images are persisted (public) like a reply.
 */
export async function saveAgentDraft(
  name: string,
  body: string,
  bodyHtml?: string,
): Promise<Ticket> {
  const html = await persistInlineMedia(bodyHtml);
  return frappeMethod<Ticket>(`${AGENT_API}.save_agent_draft`, { name, body, bodyHtml: html });
}

/** Promote the saved draft to a real agent reply (THIS is the send), then clear it. john-only. */
export async function sendAgentDraft(name: string): Promise<Ticket> {
  return frappeMethod<Ticket>(`${AGENT_API}.send_agent_draft`, { name });
}

/** Discard the agent-only draft without sending. john-only. Customer sees nothing. */
export async function discardAgentDraft(name: string): Promise<Ticket> {
  return frappeMethod<Ticket>(`${AGENT_API}.discard_agent_draft`, { name });
}
